import { Injectable } from '@angular/core';
import {
    AttachmentHandler,
    CurrentAttachmentVersionDto,
    OrganisationAuthorityService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import {
    BehaviorSubject,
    ReplaySubject,
    Subject,
    combineLatest,
    forkJoin,
    from,
    of
} from 'rxjs';
import { catchError, first, map, takeUntil } from 'rxjs/operators';

export interface AuthorityDocumentGroup {
    authorityId: number;
    sourceName: string;
    handler: AttachmentHandler;
}

@Injectable()
export class AuthorityDocumentsDataService {
    private workingSource = new ReplaySubject<boolean>(1);
    private groupsSource = new BehaviorSubject<AuthorityDocumentGroup[]>([]);

    working$ = this.workingSource.asObservable();
    groups$ = this.groupsSource.asObservable();

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    /**
     * The person ID used for acknowledgement checks, stored so that
     * recheckGroup can re-use it after handler.change$ fires.
     */
    private personId: number;

    constructor(
        private organisationAuthorityService: OrganisationAuthorityService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => this.workingSource.next(working));
    }

    ngOnDestroy() {
        this.workingSource.complete();
        this.groupsSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    loadGroups(orgId: number, personId: number) {
        this.personId = personId;
        const doneWorking = this.workTracker.createTracker();

        this.organisationAuthorityService
            .findAuthorities(orgId, orgId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: groups => {
                    const authorities = groups.flatMap(g =>
                        (g.authorities ?? []).map(a => ({
                            id: a.id,
                            // Use the user-assigned name if set, otherwise fall back to the authority type name
                            name: a.name || g.name,
                            handler: this.organisationAuthorityService.attachmentHandler(
                                a.id,
                                orgId
                            )
                        }))
                    );

                    if (authorities.length === 0) {
                        this.groupsSource.next([]);
                        doneWorking();
                        return;
                    }

                    // Check each authority for unacknowledged docs
                    const checks$ = authorities.map(auth =>
                        combineLatest([
                            auth.handler.attachments$.pipe(
                                map((docs: CurrentAttachmentVersionDto[]) =>
                                    docs.filter(d => d.requiresAcknowledgement)
                                )
                            ),
                            from(auth.handler.findAcknowledgements([personId]))
                        ]).pipe(
                            first(),
                            map(([docs, acks]) => {
                                const ackedVersionIds = new Set(
                                    acks.map(a => a.attachmentVersionId)
                                );
                                const hasUnacknowledged = docs.some(
                                    d =>
                                        !ackedVersionIds.has(
                                            d.attachmentVersionId
                                        )
                                );
                                if (!hasUnacknowledged) {
                                    return null;
                                }
                                return {
                                    authorityId: auth.id,
                                    sourceName: auth.name,
                                    handler: auth.handler
                                } as AuthorityDocumentGroup;
                            }),
                            catchError(() => of(null as AuthorityDocumentGroup))
                        )
                    );

                    forkJoin(checks$)
                        .pipe(takeUntil(this.ngUnsubscribe$))
                        .subscribe({
                            next: results => {
                                const activeGroups = results.filter(
                                    g => g != null
                                );
                                this.groupsSource.next(activeGroups);
                                doneWorking();

                                // Watch each handler for changes so we can hide
                                // the group once all its docs are acknowledged
                                activeGroups.forEach(group => {
                                    group.handler.change$
                                        .pipe(takeUntil(this.ngUnsubscribe$))
                                        .subscribe(() =>
                                            this.recheckGroup(group)
                                        );
                                });
                            },
                            error: () => {
                                this.groupsSource.next([]);
                                doneWorking();
                            }
                        });
                },
                error: () => {
                    this.groupsSource.next([]);
                    doneWorking();
                }
            });
    }

    /**
     * Re-checks whether a group still has unacknowledged docs after a
     * change (e.g. an acknowledgement) has been made via the attachment-list.
     * Removes the group from the list when all docs are acknowledged.
     */
    private recheckGroup(group: AuthorityDocumentGroup) {
        combineLatest([
            group.handler.attachments$.pipe(
                first(),
                map((docs: CurrentAttachmentVersionDto[]) =>
                    docs.filter(d => d.requiresAcknowledgement)
                )
            ),
            from(group.handler.findAcknowledgements([this.personId]))
        ])
            .pipe(first(), takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: ([docs, acks]) => {
                    const ackedVersionIds = new Set(
                        acks.map(a => a.attachmentVersionId)
                    );
                    const hasUnacknowledged = docs.some(
                        d => !ackedVersionIds.has(d.attachmentVersionId)
                    );
                    if (!hasUnacknowledged) {
                        const current = this.groupsSource.getValue();
                        this.groupsSource.next(
                            current.filter(
                                g => g.authorityId !== group.authorityId
                            )
                        );
                    }
                }
            });
    }
}
